import { AccountSelect } from '@components'

import { Divider, Input, Space, Text } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormContext } from '../../../../../../../../../../hooks'
import { CreateAccountFromTemplateFormSchema } from '../../../../types'
import {
  AccountDescriptionFormItem,
  AccountNameFormItem,
  AccountNumberFieldLabel,
} from '../../../AccountFromTemplateFormFields'
import { RequiredFieldLabel } from '../../../RequiredFieldLabel'
import { useCreateAccountFromTemplateForm } from '../../contexts'

export const CreateAccountFromTemplateForm = (): ReactElement => {
  const { t } = useTranslation()
  const { FormItem } = useFormContext<CreateAccountFromTemplateFormSchema>()
  const {
    accountNumberCaption,
    excludedAccountIds,
    Form,
    handleAccountNumberBlur,
    handleAccountNumberChange,
    handleBaseAccountSelect,
    handleManualEntryChange,
    hasBaseAccount,
    isManualEntryEnabled,
    natures,
    selectedBaseAccountId,
  } = useCreateAccountFromTemplateForm()

  const handleAccountNumberInputBlur = useCallback(
    (onBlur: () => void, value: number | null) => {
      onBlur()
      handleAccountNumberBlur(value)
    },
    [handleAccountNumberBlur],
  )

  const handleAccountNumberInputChange = useCallback(
    (onChange: (value: number | null) => void, event: ChangeEvent<HTMLInputElement>) => {
      const nextAccountNumber = event.target.value === '' ? null : Number(event.target.value)
      onChange(nextAccountNumber)
      handleAccountNumberChange()
    },
    [handleAccountNumberChange],
  )

  return (
    <Form>
      <FormItem
        label={<RequiredFieldLabel>{t('coa.account_from_base_form.base_account')}</RequiredFieldLabel>}
        name="baseAccountId"
        render={({ field: { onChange, value, ...field } }) => (
          <AccountSelect
            {...field}
            allowClear
            dropdownSize="fitTrigger"
            excludedAccounts={excludedAccountIds}
            natures={natures}
            onSelect={handleBaseAccountSelect}
            selectedId={selectedBaseAccountId}
            size="sm"
          />
        )}
      />
      <Space size="s4" />
      <Text colorVariant="secondary" variant="bodySM">
        {t('coa.account_from_base_form.base_account.description')}
      </Text>

      {hasBaseAccount && (
        <>
          <Space size="s24" />
          <Divider />
          <Space size="s24" />

          <AccountNameFormItem />

          <Space size="s24" />

          <FormItem
            extra={accountNumberCaption}
            label={
              <AccountNumberFieldLabel
                isManualEntryEnabled={isManualEntryEnabled}
                onManualEntryChange={handleManualEntryChange}
              />
            }
            name="accountNumber"
            render={({ field: { onBlur, onChange, value, ...field } }) => (
              <Input
                {...field}
                disabled={!isManualEntryEnabled}
                onBlur={() => handleAccountNumberInputBlur(onBlur, value)}
                onChange={(event) => handleAccountNumberInputChange(onChange, event)}
                size="sm"
                type="number"
                value={value ?? ''}
              />
            )}
          />

          <Space size="s24" />

          <AccountDescriptionFormItem />
        </>
      )}
    </Form>
  )
}
